import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { startGoogleSignIn, useSession } from '../lib/auth'
import { CourtBackground } from '../components/CourtBackground'
import ThemeToggle from '../components/ThemeToggle'
import Mark from '../components/Mark'

// M5 Phase 5: role-select fork on the landing page. User picks agent or
// coach/scout (single-select), then hits one Continue with Google button.
// The picked role rides through OAuth via `?intended_role=` on the
// callback URL; AuthCallback forwards it to /onboarding which pre-selects
// the matching radio. Selection is still a confirmation step on
// /onboarding — role is single-shot per spec §7, so we never auto-set
// it from a URL param.
//
// Persistent-sessions follow-up: once we moved to 1y sliding sessions,
// beta testers who closed the tab and reopened hooproster.com landed on
// THIS page even though their cookie was still valid — and the picker
// looked identical to being logged out. We now check useSession() at
// the top and redirect authenticated users to /dashboard. Anonymous
// users still see the picker; the brief pending window renders the same
// background to avoid a flash-of-landing for signed-in returners.
//
// Sign-up / sign-in split: the role fork is a SIGN-UP affordance. It used to
// be the only door — the Google button stayed disabled until a card was
// picked, so returning users had to pick a role that AuthCallback then threw
// away. The secondary "Already have an account? Sign in" control below the
// CTA starts OAuth with no `intended_role` and is enabled with nothing
// selected. Both paths share `submitting` / `err`.

type IntendedRole = 'agent' | 'coach'

const CHOICES: { value: IntendedRole; title: string; subtitle: string }[] = [
  {
    value: 'agent',
    title: "I'm an agent",
    subtitle: 'List your players, build your roster.',
  },
  {
    value: 'coach',
    // M6.2-followup-2 — corpus is ~91% European, ~5% Oceania, ~4% Asia
    // post-M6.1 enrichment; "across Asia" was actively misleading.
    title: "I'm a coach or scout",
    subtitle: 'Find verified talent across international leagues.',
  },
]

export default function Landing() {
  const session = useSession()
  const [selected, setSelected] = useState<IntendedRole | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Signed-in user landed on `/` (typed root, came from a bookmark, etc.) —
  // send them straight to the dashboard.
  if (session.data?.user) {
    return <Navigate to="/dashboard" replace />
  }
  // While the session check is in flight, render the same background-only
  // stage so a returning user doesn't see the picker flash before the
  // redirect fires. The window is normally a single tick (cookieCache
  // hit), at worst one D1 lookup.
  if (session.isPending) {
    return (
      <main className="relative min-h-screen flex items-center justify-center px-6 py-12 bg-hoop-bg">
        <CourtBackground className="absolute inset-0 text-hoop-text opacity-[0.07] dark:opacity-[0.08]" />
      </main>
    )
  }

  async function runSignIn(intendedRole?: IntendedRole) {
    if (submitting) return
    setSubmitting(true)
    setErr(null)
    try {
      await startGoogleSignIn(intendedRole)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'oauth_failed')
      setSubmitting(false)
    }
  }

  // Sign-up path — the role fork above rides through OAuth as a hint.
  function continueWithGoogle() {
    if (!selected) return
    void runSignIn(selected)
  }

  // Sign-in path — deliberately no role hint, and deliberately not gated on
  // the fork. A returning user's role is already set server-side; making them
  // re-pick was pure friction (and the pick was discarded by AuthCallback
  // anyway). This is the whole point of the control being enabled with
  // nothing selected.
  function signInExisting() {
    void runSignIn()
  }

  return (
    <main className="relative min-h-screen flex items-center justify-center px-6 py-12 bg-hoop-bg">
      <CourtBackground className="absolute inset-0 text-hoop-text opacity-[0.07] dark:opacity-[0.08]" />
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>
      <div className="relative z-10 max-w-2xl w-full">
        <div className="text-center mb-10">
          <Mark className="h-12 w-auto mx-auto mb-4 text-hoop-text" />
          <div className="text-sm uppercase tracking-widest text-hoop-text-3 mb-3">
            HoopRoster
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-hoop-text mb-3">
            Verified roster, real references.
          </h1>
          <p className="text-base md:text-lg text-hoop-text-2 max-w-xl mx-auto">
            LinkedIn for international basketball. Agent-managed player profiles,
            built for coaches and recruiters who want to find under-the-radar talent.
          </p>
        </div>

        <fieldset className="space-y-3 mb-6">
          <legend className="sr-only">I am a…</legend>
          {CHOICES.map((c) => (
            <ChoiceCard
              key={c.value}
              value={c.value}
              title={c.title}
              subtitle={c.subtitle}
              selected={selected === c.value}
              onSelect={() => setSelected(c.value)}
            />
          ))}
        </fieldset>

        <button
          type="button"
          onClick={continueWithGoogle}
          disabled={!selected || submitting}
          className="w-full bg-hoop-primary text-hoop-primary-fg px-4 py-3 rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? 'Redirecting…' : 'Continue with Google'}
        </button>

        <p className="text-center text-sm text-hoop-text-3 mt-4">
          Already have an account?{' '}
          <button
            type="button"
            onClick={signInExisting}
            disabled={submitting}
            className="underline font-medium text-hoop-text-2 hover:text-hoop-text disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Sign in
          </button>
        </p>

        <p className="text-center text-sm text-hoop-text-3 mt-6">
          Just browsing?{' '}
          <Link
            to="/discover"
            className="underline hover:text-hoop-text text-hoop-text-2"
          >
            Discover players
          </Link>{' '}
          — no account needed.
        </p>

        {err && (
          <p className="text-center mt-4 text-sm text-hoop-red">
            {err}
          </p>
        )}
      </div>
    </main>
  )
}

function ChoiceCard({
  value,
  title,
  subtitle,
  selected,
  onSelect,
}: {
  value: IntendedRole
  title: string
  subtitle: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <label
      className={
        'flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors bg-hoop-surface ' +
        (selected
          ? 'border-hoop-primary shadow-sm'
          : 'border-hoop-border hover:border-hoop-accent')
      }
    >
      <input
        type="radio"
        name="intended_role"
        value={value}
        checked={selected}
        onChange={onSelect}
        className="mt-1 h-4 w-4 accent-hoop-primary border-hoop-border focus:ring-hoop-accent"
      />
      <div className="min-w-0">
        <div className="font-medium text-hoop-text">
          {title}
        </div>
        <div className="text-sm text-hoop-text-2 mt-0.5">
          {subtitle}
        </div>
      </div>
    </label>
  )
}
